import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs } from 'firebase/firestore';
import {
    AcademicCapIcon,
    BellIcon,
    CalendarIcon,
    CurrencyDollarIcon,
    ExclamationTriangleIcon,
    ChatBubbleLeftRightIcon,
    ArrowRightOnRectangleIcon,
    ArrowUturnLeftIcon
} from '@heroicons/react/24/outline';
import { Spinner } from './components/Spinner';
import ParentLoginPage from './components/ParentLoginPage';

// Modal لعرض تفاصيل الواجب
const HomeworkDetailsModal = ({ isOpen, onClose, homework }) => {
    if (!isOpen || !homework) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-lg text-right relative" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
                <h2 className="text-2xl font-bold mb-4 text-gray-800">{homework.title}</h2>
                <div className="mb-2 text-gray-700"><span className="font-semibold">المادة:</span> {homework.subject}</div>
                <div className="mb-2 text-gray-700"><span className="font-semibold">الصف:</span> {homework.grade}</div>
                <div className="mb-2 text-gray-700"><span className="font-semibold">تاريخ التسليم:</span> {homework.dueDate}</div>
                <div className="mb-4 text-gray-700"><span className="font-semibold">الوصف:</span> {homework.description}</div>
                {homework.file && (
                    <div className="mb-4">
                        <a href={homework.file} target="_blank" rel="noopener noreferrer" className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition">عرض/تحميل الملف المرفق</a>
                    </div>
                )}
                <div className="flex justify-end gap-2 mt-6">
                    <button className="px-6 py-2 rounded-lg bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition" onClick={onClose}>إغلاق</button>
                </div>
            </div>
        </div>
    );
};

// ParentDashboard component - Shows only essential student information
const ParentDashboard = ({ student, schoolData, onLogout, childrenStudents, onChildSelect, onBackToChildSelection, selectedHomework, setSelectedHomework }) => {
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const { enrollments, fees, classes, timetable, subjects } = schoolData;

    // Get the student's class
    const enrollment = enrollments?.find(e => e.studentId === student?.id);
    const studentClass = enrollment ? classes?.find(c => c.id === enrollment.classId) : null;

    // Get only unpaid and current month's fees
    const currentMonth = new Date().getMonth();
    const studentFees = fees?.filter(f =>
        f.studentId === student?.id &&
        (f.status !== 'paid' || new Date(f.dueDate).getMonth() === currentMonth)
    ).sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));

    // Get only today's timetable
    const days = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    const today = days[new Date().getDay()];
    const todaySchedule = studentClass
        ? timetable?.filter(t => t.classId === studentClass.id && t.day === today)
            .map(session => ({
                ...session,
                subject: subjects?.find(s => s.id === session.subjectId)?.name || '',
            }))
            .sort((a, b) => a.time.localeCompare(b.time))
        : [];

    const getStatusChip = (status) => {
        switch (status) {
            case 'paid': return <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full">مدفوع</span>;
            case 'unpaid': return <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-200 rounded-full">غير مدفوع</span>;
            case 'overdue': return <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-200 rounded-full">متأخر</span>;
            default: return null;
        }
    };

    if (!student) {
        return <div className="text-center p-8">لا توجد معلومات متاحة</div>;
    }

    return (
        <div className="min-h-screen bg-gray-100" dir="rtl">
            {/* Header with Student Info */}
            <div className="bg-gradient-to-b from-yellow-500 to-purple-600 text-white p-6 pb-32">
                <div className="flex flex-col items-center mb-6">
                    <div className="flex flex-col items-center gap-3 mb-4">
                        <img
                            src={student.photoUrl || '/logo192.png'}
                            alt={student.firstName}
                            className="w-14 h-14 rounded-full border-2 border-white/40 shadow-lg"
                        />
                        <div className="text-center">
                            <h2 className="text-xl font-bold text-white">
                                {student.firstName} {student.lastName}
                            </h2>
                            <div className="mt-1">
                                <span className="text-sm bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                                    {studentClass?.name || 'غير مسجل في قسم'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-3 mt-2">
                        {childrenStudents.length > 1 && (
                            <button
                                onClick={onBackToChildSelection}
                                className="bg-yellow-400 hover:bg-yellow-500 text-purple-900 px-4 py-1.5 rounded-full text-sm flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <ArrowUturnLeftIcon className="w-4 h-4" />
                                العودة لاختيار التلميذ
                            </button>
                        )}
                        <button
                            onClick={() => setShowLogoutModal(true)}
                            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-1.5 rounded-full text-sm flex items-center gap-2 transition-colors shadow-sm"
                        >
                            <ArrowRightOnRectangleIcon className="w-4 h-4" />
                            تسجيل الخروج
                        </button>
                    </div>
                </div>

                {/* Modal تأكيد تسجيل الخروج */}
                {showLogoutModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4 transform transition-all"
                            onClick={(e) => e.stopPropagation()}>
                            <div className="text-center">
                                <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
                                <h3 className="text-lg font-bold text-gray-900 mb-2">تأكيد تسجيل الخروج</h3>
                                <p className="text-sm text-gray-600 mb-6">
                                    هل أنت متأكد أنك تريد تسجيل الخروج من حسابك؟
                                </p>
                            </div>
                            <div className="flex justify-center gap-3">
                                <button
                                    onClick={() => {
                                        setShowLogoutModal(false);
                                        onLogout();
                                    }}
                                    className="px-4 py-2 bg-purple-500 text-white text-sm font-semibold rounded-lg hover:bg-purple-600 transition-colors"
                                >
                                    نعم، تسجيل الخروج
                                </button>
                                <button
                                    onClick={() => setShowLogoutModal(false)}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    إلغاء
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="px-4 -mt-24">
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    {/* 1. Notifications */}
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <div className="flex items-center gap-4 mb-6">
                            <div>
                                <div className="flex items-center gap-3">
                                    <BellIcon className="w-6 h-6 text-yellow-600" />
                                    <h3 className="text-lg font-bold text-gray-800">الإشعارات</h3>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">آخر التحديثات والتنبيهات</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {schoolData.notifications?.length > 0 ? (
                                schoolData.notifications
                                    .filter(notification => {
                                        const recipient = notification.recipients;
                                        if (!recipient) return false;

                                        // إشعار لجميع التلاميذ
                                        if (recipient.type === 'all_students') return true;

                                        // إشعار لجميع الأقسام
                                        if (recipient.type === 'all_classes') return true;

                                        // إشعار مخصص لقسم معين
                                        if (recipient.type === 'class' && recipient.classId === studentClass?.id) return true;

                                        // إشعار مخصص لتلميذ معين
                                        if (recipient.type === 'student' && recipient.studentId === student?.id) return true;

                                        // إشعار مخصص لعدة تلاميذ
                                        if (recipient.type === 'students' && recipient.studentIds?.includes(student?.id)) return true;

                                        return false;
                                    })
                                    .sort((a, b) => b.createdAt - a.createdAt)
                                    .map(notification => (
                                        <div key={notification.id}
                                            className={`p-4 rounded-xl transition-all duration-200 hover:shadow-md ${notification.status === 'unread'
                                                ? 'bg-gradient-to-r from-yellow-50 to-purple-50 border border-yellow-200'
                                                : 'bg-gray-50 border border-gray-200'
                                                }`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className={`p-2 rounded-full ${notification.type === 'admin' ? 'bg-yellow-100' : 'bg-blue-100'}`}>
                                                    {notification.type === 'admin' ? (
                                                        <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                                                    ) : (
                                                        <AcademicCapIcon className="w-5 h-5 text-blue-600" />
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-bold text-gray-800">{notification.title}</p>
                                                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{notification.message}</p>
                                                    <div className="flex items-center gap-2 mt-3">
                                                        <div className="h-1 w-1 bg-gray-400 rounded-full"></div>
                                                        <p className="text-xs text-gray-500">
                                                            {new Date(notification.createdAt).toLocaleDateString('ar-TN', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </p>
                                                    </div>

                                                    {/* Display attached file if exists */}
                                                    {notification.attachment && (
                                                        <div className="mt-3">
                                                            {notification.attachment.type?.startsWith('image/') ? (
                                                                <div className="relative group">
                                                                    <img
                                                                        src={notification.attachment.url}
                                                                        alt={notification.attachment.name}
                                                                        className="max-h-48 w-auto rounded-lg border border-gray-200 shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                                                                        onClick={() => window.open(notification.attachment.url, '_blank')}
                                                                    />
                                                                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                                                        📎 {notification.attachment.name}
                                                                    </p>
                                                                </div>
                                                            ) : (
                                                                <a
                                                                    href={notification.attachment.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                                                                >
                                                                    📄 {notification.attachment.name}
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                                    </svg>
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                            ) : (
                                <div className="text-center py-8">
                                    <div className="inline-flex p-4 rounded-full bg-gray-100 mb-3">
                                        <BellIcon className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <p className="text-gray-500">لا توجد إشعارات جديدة</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 2. Homework Card */}
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-xl">
                                    <AcademicCapIcon className="w-6 h-6 text-purple-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">الواجبات المدرسية</h3>
                                    <p className="text-sm text-gray-500 mt-1">الواجبات مجمعة حسب القسم</p>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-6 max-h-96 overflow-y-auto">
                            {(() => {
                                const augmentedChildren = (childrenStudents || []).map(child => {
                                    const enrollment = schoolData.enrollments?.find(e => e.studentId === child.id);
                                    const classInfo = enrollment ? schoolData.classes?.find(c => c.id === enrollment.classId) : null;
                                    return { ...child, className: classInfo?.name || 'غير محدد' };
                                });

                                const homeworksByClass = (schoolData?.homeworks || []).reduce((acc, hw) => {
                                    const className = hw.grade || 'غير محدد';
                                    if (!acc[className]) {
                                        acc[className] = [];
                                    }
                                    acc[className].push(hw);
                                    return acc;
                                }, {});

                                const childrenByClass = augmentedChildren.reduce((acc, child) => {
                                    const className = child.className;
                                    if (!acc[className]) {
                                        acc[className] = [];
                                    }
                                    acc[className].push(child);
                                    return acc;
                                }, {});

                                const relevantClasses = [...new Set(augmentedChildren.map(child => child.className))];

                                if (relevantClasses.length === 0) {
                                    return <p className="text-gray-600">لا يوجد تلامذة لعرض الواجبات.</p>;
                                }

                                return (
                                    <>
                                        {relevantClasses.map(className => {
                                            const homeworks = homeworksByClass[className] || [];
                                            const children = childrenByClass[className] || [];
                                            if (children.length === 0) return null;
                                            return (
                                                <div key={className} className="mb-4">
                                                    <div className="font-bold text-purple-700 mb-2">
                                                        القسم: {className}
                                                        <span className="text-xs text-gray-500 ml-2">
                                                            ({children.map(c => `${c.firstName} ${c.lastName}`).join(', ')})
                                                        </span>
                                                    </div>
                                                    {homeworks.length > 0 ? (
                                                        <ul className="space-y-2">
                                                            {homeworks.map(hw => (
                                                                <li key={hw.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-right cursor-pointer hover:bg-yellow-50 transition" onClick={() => setSelectedHomework(hw)}>
                                                                    <div className="font-bold text-base text-gray-800">{hw.title}</div>
                                                                    <div className="text-sm text-gray-600">{hw.subject} - المادة</div>
                                                                    <div className="text-xs text-gray-500">تاريخ التسليم: {hw.dueDate}</div>
                                                                    <div className="mt-1 text-sm line-clamp-2">{hw.description}</div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-gray-500">لا توجد واجبات لهذا القسم حالياً.</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <HomeworkDetailsModal isOpen={!!selectedHomework} onClose={() => setSelectedHomework(null)} homework={selectedHomework} />
                                    </>
                                );
                            })()}
                        </div>
                    </div>

                    {/* 3. Today's Schedule */}
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-xl">
                                    <CalendarIcon className="w-6 h-6 text-purple-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">جدول اليوم</h3>
                                    <p className="text-sm text-gray-500 mt-1">{new Date().toLocaleDateString('ar-TN', { weekday: 'long' })}</p>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {todaySchedule.length > 0 ? (
                                todaySchedule.map(session => (
                                    <div key={session.id} className="flex flex-row-reverse justify-between items-center p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                            <span className="font-medium">{session.subject}</span>
                                        </div>
                                        <span className="text-sm text-gray-600 font-mono text-right">{session.time}</span>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-gray-500 py-8">لا توجد حصص مجدولة لهذا اليوم</p>
                            )}
                        </div>
                    </div>

                    {/* 4. Financial Status */}
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-yellow-100 rounded-xl">
                                    <CurrencyDollarIcon className="w-6 h-6 text-yellow-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">الأجور المالية</h3>
                                    <p className="text-sm text-gray-500 mt-1">مواعيد الخلاص والشهر الحالي</p>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {studentFees?.length > 0 ? (
                                studentFees.map(fee => (
                                    <div key={fee.id} className="flex flex-row-reverse justify-between items-start p-3 bg-gray-50 rounded-lg">
                                        <div className="text-right">
                                            <p className="font-medium">{fee.description}</p>
                                            <p className="text-sm text-gray-500 mt-1">
                                                تاريخ الخلاص: {new Date(fee.dueDate).toLocaleDateString('ar-TN')}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono text-lg">{fee.amount.toFixed(2)} د.ت</div>
                                            <div className="mt-1">{getStatusChip(fee.status)}</div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-gray-500 py-8">لا توجد اجور مالية حالياً</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main Parent Portal component
const ParentPortal = ({ schoolSettings, db, appId, onBackToLanding }) => {
    // استخدام localStorage لحفظ حالة تسجيل الدخول
    const [studentAuthData, setStudentAuthData] = useState(() => {
        const saved = localStorage.getItem('parentAuthData');
        return saved ? JSON.parse(saved) : null;
    });
    const [schoolData, setSchoolData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [childrenStudents, setChildrenStudents] = useState(() => {
        const saved = localStorage.getItem('childrenStudents');
        return saved ? JSON.parse(saved) : [];
    });
    const [selectedChild, setSelectedChild] = useState(() => {
        const saved = localStorage.getItem('selectedChild');
        return saved ? JSON.parse(saved) : null;
    });
    const [selectedHomework, setSelectedHomework] = useState(null);
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    // حفظ البيانات في localStorage عند تغييرها
    useEffect(() => {
        if (studentAuthData) {
            localStorage.setItem('parentAuthData', JSON.stringify(studentAuthData));
        }
    }, [studentAuthData]);

    useEffect(() => {
        if (childrenStudents.length > 0) {
            localStorage.setItem('childrenStudents', JSON.stringify(childrenStudents));
        }
    }, [childrenStudents]);

    useEffect(() => {
        if (selectedChild) {
            localStorage.setItem('selectedChild', JSON.stringify(selectedChild));
        }
    }, [selectedChild]);

    useEffect(() => {
        const loadSchoolData = async () => {
            // تحقق من وجود بيانات تسجيل الدخول في localStorage
            const savedAuthData = localStorage.getItem('parentAuthData');
            const savedChildren = localStorage.getItem('childrenStudents');
            const savedSelectedChild = localStorage.getItem('selectedChild');

            if (savedAuthData && !studentAuthData) {
                setStudentAuthData(JSON.parse(savedAuthData));
            }
            if (savedChildren && childrenStudents.length === 0) {
                setChildrenStudents(JSON.parse(savedChildren));
            }
            if (savedSelectedChild && !selectedChild) {
                setSelectedChild(JSON.parse(savedSelectedChild));
            }
        };
        loadSchoolData();
    }, []);

    useEffect(() => {
        if (studentAuthData && selectedChild) {
            const loadSchoolData = async () => {
                setIsLoading(true);
                try {
                    const [enrollments, fees, classes, timetable, subjects, homeworks, notifications] = await Promise.all([
                        getDocs(collection(db, `artifacts/${appId}/public/data/enrollments`)),
                        getDocs(collection(db, `artifacts/${appId}/public/data/fees`)),
                        getDocs(collection(db, `artifacts/${appId}/public/data/classes`)),
                        getDocs(collection(db, `artifacts/${appId}/public/data/timetable`)),
                        getDocs(collection(db, `artifacts/${appId}/public/data/subjects`)),
                        getDocs(collection(db, 'homeworks')),
                        getDocs(collection(db, 'notifications'))
                    ]);

                    setSchoolData({
                        enrollments: enrollments.docs.map(doc => ({ id: doc.id, ...doc.data() })) || [],
                        fees: fees.docs.map(doc => ({ id: doc.id, ...doc.data() })) || [],
                        classes: classes.docs.map(doc => ({ id: doc.id, ...doc.data() })) || [],
                        timetable: timetable.docs.map(doc => ({ id: doc.id, ...doc.data() })) || [],
                        subjects: subjects.docs.map(doc => ({ id: doc.id, ...doc.data() })) || [],
                        homeworks: (homeworks.docs.map(doc => {
                            const data = doc.data();
                            return {
                                id: doc.id,
                                ...data,
                                createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : new Date(0))
                            };
                        }) || []).sort((a, b) => b.createdAt - a.createdAt),
                        notifications: notifications.docs.map(doc => ({
                            id: doc.id,
                            ...doc.data(),
                            createdAt: doc.data().createdAt?.toDate() || new Date(),
                            recipients: doc.data().recipients || { type: 'none' }
                        })) || []
                    });
                } catch (error) {
                    console.error('Error loading school data:', error);
                } finally {
                    setIsLoading(false);
                }
            };
            loadSchoolData();
        }
    }, [studentAuthData, selectedChild, db, appId]);

    const handleLogin = (authData) => {
        setStudentAuthData(authData);
        localStorage.setItem('parentAuthData', JSON.stringify(authData));

        const children = authData.childrenStudents || [];
        setChildrenStudents(children);
        localStorage.setItem('childrenStudents', JSON.stringify(children));

        if (children.length === 1) {
            setSelectedChild(children[0]);
            localStorage.setItem('selectedChild', JSON.stringify(children[0]));
        } else if (children.length > 1) {
            setSelectedChild(null);
            localStorage.removeItem('selectedChild');
        }
    };

    const handleLogout = () => {
        setStudentAuthData(null);
        setSchoolData(null);
        setChildrenStudents([]);
        setSelectedChild(null);

        // حذف جميع بيانات الجلسة
        localStorage.removeItem('parentAuthData');
        localStorage.removeItem('childrenStudents');
        localStorage.removeItem('selectedChild');
    };

    const handleChildSelect = (child) => {
        setSelectedChild(child);
        localStorage.setItem('selectedChild', JSON.stringify(child));
    };

    if (!studentAuthData) {
        return (
            <ParentLoginPage
                onLogin={handleLogin}
                schoolSettings={schoolSettings}
                onBackToLanding={onBackToLanding}
                db={db}
                appId={appId}
            />
        );
    }

    if (childrenStudents.length > 1 && !selectedChild) {
        return (
            <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center p-4" dir="rtl">
                <div className="w-full max-w-md">
                    <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
                        <h1 className="text-2xl font-bold text-gray-800 mb-6">اختر تلميذاً لعرض لوحة التحكم</h1>
                        <div className="space-y-4">
                            {childrenStudents.map(child => (
                                <button
                                    key={child.id}
                                    onClick={() => handleChildSelect(child)}
                                    className="w-full flex flex-row-reverse items-center gap-4 p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                                >
                                    <img src={child.photoUrl || '/logo192.png'} alt={child.firstName} className="w-12 h-12 rounded-full border-2 border-blue-200" />
                                    <span className="text-lg font-medium text-gray-800 text-right">{child.firstName} {child.lastName}</span>
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setShowLogoutModal(true)}
                            className="mt-6 text-gray-600 hover:text-red-600 text-sm flex items-center justify-center w-full gap-2"
                        >
                            تسجيل الخروج
                        </button>
                        {showLogoutModal && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                                <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm text-center mx-auto" style={{ position: 'relative', top: '0', left: '0', transform: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
                                    <h2 className="text-lg font-bold mb-4">هل أنت متأكد أنك تريد تسجيل الخروج؟</h2>
                                    <div className="flex justify-center gap-4 mt-6">
                                        <button
                                            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                                            onClick={() => {
                                                setShowLogoutModal(false);
                                                handleLogout();
                                            }}
                                        >
                                            تأكيد
                                        </button>
                                        <button
                                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                            onClick={() => setShowLogoutModal(false)}
                                        >
                                            إلغاء
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (isLoading || !schoolData) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Spinner /> <span className="mr-3">جاري تحميل البيانات...</span>
            </div>
        );
    }

    return (
        <ParentDashboard
            student={selectedChild}
            schoolData={schoolData}
            onLogout={handleLogout}
            childrenStudents={childrenStudents}
            onChildSelect={handleChildSelect}
            onBackToChildSelection={() => setSelectedChild(null)}
            selectedHomework={selectedHomework}
            setSelectedHomework={setSelectedHomework}
        />
    );
};

ParentPortal.propTypes = {
    schoolSettings: PropTypes.shape({
        schoolName: PropTypes.string,
        schoolLogo: PropTypes.string
    }).isRequired,
    db: PropTypes.object.isRequired,
    onBackToLanding: PropTypes.func
};

export default ParentPortal;